import { Button } from 'blaze-ui/Button'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Upload5 } from 'blaze-ui/icons'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'

interface Props extends Omit<ButtonProps, 'onPress' | 'isDisabled'> {
  brandKitId: number
  label?: string
  onSuccess?: () => void
  accept?: string
}

export const UploadButton = ({
  brandKitId,
  variant = 'tertiary',
  size = 'xs',
  frontIcon = Upload5,
  label = 'Upload',
  onSuccess,
  accept = ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
  ...buttonProps
}: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const imageLibrary = useCurrentImageLibrary()

  const { uploadImageFiles, isUploading } = useImportMediaFiles(false, () => {
    onSuccess?.()
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
    if (imageLibrary?.key) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    }
  })

  return (
    <FileTrigger
      onSelect={(fileList) => {
        if (!fileList || !imageLibrary?.key) return

        return uploadImageFiles(Array.from(fileList), imageLibrary.key, { brand_kit_id: brandKitId })
      }}
      accept={accept}
      allowsMultiple={true}
      aria-label="Upload Media"
    >
      <Button
        variant={variant}
        size={size}
        frontIcon={frontIcon}
        isDisabled={isUploading || !imageLibrary?.key}
        aria-label="Upload Media"
        {...buttonProps}
      >
        {isUploading ? 'Uploading...' : label}
      </Button>
    </FileTrigger>
  )
}
